import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useJumpSearch } from '@opallabs/beeblebrox'
import { MessageKeys, useMessage } from '@opallabs/hooks'
import { Sidebar } from '@opallabs/lapidary'
import { gemCopilotOpenSelector } from './shared'
import { usePanelWidth } from './usePanelWidth'

// A Gem-authored opener seeded when the copilot opens itself (e.g. plan
// onboarding). `suggestions` become clickable starter chips under the intro.
export type CopilotIntro = { intro: string; suggestions?: Array<string> }

interface CopilotLayoutContextValue {
  isOpen: boolean
  panelWidth: number
  setPanelWidth: (width: number) => void
  previewPanelWidth: (width: number) => void
  kickoffCopilotIntro: CopilotIntro | null
  openCopilot: () => void
  closeCopilot: () => void
  toggleCopilot: () => void
  setKickoffCopilotIntro: (intro: CopilotIntro | null) => void
  openCopilotWithIntro: (intro: string, suggestions?: Array<string>) => void
}

const CopilotLayoutContext = createContext<CopilotLayoutContextValue | null>(
  null
)

export const CopilotLayoutProvider: React.FC = ({ children }) => {
  const { open: jumpSearchOpen } = useJumpSearch()
  const { panelWidth, setPanelWidth, previewPanelWidth } = usePanelWidth()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [kickoffCopilotIntro, setKickoffCopilotIntro] =
    useState<CopilotIntro | null>(null)

  const openCopilot = () => setIsOpen(true)
  const closeCopilot = () => setIsOpen(false)
  const toggleCopilot = () => setIsOpen((prev) => !prev)
  const openCopilotWithIntro = (intro: string, suggestions?: Array<string>) => {
    setKickoffCopilotIntro({ intro, suggestions })
    setIsOpen(true)
  }

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
  }

  useMessage(MessageKeys.openCopilot, () => {
    openCopilot()
  })
  useMessage(MessageKeys.closeCopilot, () => {
    closeCopilot()
  })
  useMessage(MessageKeys.toggleCopilot, () => {
    toggleCopilot()
  })
  // Receiver for surfaces outside asgard (IE: monolith iframe).
  // in-tree asgard callers use openCopilotWithIntro directly.
  useMessage(MessageKeys.kickoffCopilot, (event) => {
    openCopilotWithIntro(event.data.content)
  })
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add(gemCopilotOpenSelector)
    } else {
      document.body.classList.remove(gemCopilotOpenSelector)
    }
    return () => document.body.classList.remove(gemCopilotOpenSelector)
  }, [isOpen])

  useEffect(() => {
    if (jumpSearchOpen && isOpen) {
      closeCopilot()
    }
  }, [jumpSearchOpen, isOpen])

  const value = useMemo(
    () => ({
      isOpen,
      openCopilot,
      closeCopilot,
      toggleCopilot,
      kickoffCopilotIntro,
      setKickoffCopilotIntro,
      openCopilotWithIntro,
      panelWidth,
      setPanelWidth,
      previewPanelWidth,
    }),
    [isOpen, kickoffCopilotIntro, panelWidth, setPanelWidth, previewPanelWidth]
  )
  return (
    <CopilotLayoutContext.Provider value={value}>
      <Sidebar.Root open={isOpen} onOpenChange={handleOpenChange} modal={false}>
        {children}
      </Sidebar.Root>
    </CopilotLayoutContext.Provider>
  )
}

export const useCopilotLayout = (): CopilotLayoutContextValue => {
  const ctx = useContext(CopilotLayoutContext)
  if (!ctx) {
    throw new Error(
      'useCopilotLayout must be used within a CopilotLayoutProvider'
    )
  }
  return ctx
}

/**
 * The copilot's open state, tolerating a missing provider (isolated test
 * trees mount the transport without the layout shell) by reporting a
 * never-opened copilot.
 */
export const useIsCopilotOpen = (): boolean =>
  useContext(CopilotLayoutContext)?.isOpen ?? false
